// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: label, icon and colour for each phone Chats swipe action, shared by
// the rows, the short-swipe tray and Chat settings > Swipe on chats. Colours
// match the mail swipe (MailSwipeRow MAIL_SWIPE_TONE) so a swipe means the same
// colour everywhere. A row whose action would undo itself (a pinned chat's Pin)
// shows the reverse: Unpin, Unmute, Unread, Unarchive.

import type {ChatSwipeAction} from '@app/features/channel/state/ChatsSwipePreferences';
import type {ChatRowAction} from '@app/features/channel/utils/ChatSwipeModel';
import {
	ArchiveIcon,
	BellIcon,
	BellSlashIcon,
	EnvelopeSimpleIcon,
	EnvelopeSimpleOpenIcon,
	ProhibitIcon,
	PushPinIcon,
	PushPinSlashIcon,
} from '@app/features/ui/icons/Icons';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import type React from 'react';

export const CHAT_SWIPE_ACTION_LABEL: Record<ChatSwipeAction, MessageDescriptor> = {
	read: msg({message: 'Read', comment: 'Swipe action on a phone Chats row; marks the chat read. Short tile label.'}),
	pin: msg({message: 'Pin', comment: 'Swipe action on a phone Chats row. Short tile label.'}),
	mute: msg({message: 'Mute', comment: 'Swipe action on a phone Chats row. Short tile label.'}),
	archive: msg({
		message: 'Archive',
		comment: 'Swipe action on a phone WhatsApp or mail row in Chats; archives the chat or email. Short tile label.',
	}),
	none: msg({message: 'None', comment: 'Chats swipe setting: the swipe does nothing. Short tile label.'}),
};

const REVERSE_LABEL: Record<ChatRowAction, MessageDescriptor> = {
	read: msg({message: 'Unread', comment: 'Swipe action on a phone Chats row; marks a read chat unread again.'}),
	pin: msg({message: 'Unpin', comment: 'Swipe action on a phone Chats row.'}),
	mute: msg({message: 'Unmute', comment: 'Swipe action on a phone Chats row.'}),
	archive: msg({message: 'Unarchive', comment: 'Swipe action on a phone WhatsApp row in Chats.'}),
};

/** Track colour for each action: mail's blue / amber / slate / green. */
export const CHAT_SWIPE_ACTION_TONE: Record<ChatRowAction, string> = {
	read: '#2f6fe4',
	pin: '#e8912d',
	mute: '#5b6b82',
	archive: '#2e9d5b',
};

export function chatSwipeActionLabel(action: ChatSwipeAction, reverse = false): MessageDescriptor {
	return reverse && action !== 'none' ? REVERSE_LABEL[action] : CHAT_SWIPE_ACTION_LABEL[action];
}

export function chatSwipeActionIcon(action: ChatSwipeAction, size = 22, reverse = false): React.ReactNode {
	switch (action) {
		case 'read':
			return reverse ? <EnvelopeSimpleIcon size={size} /> : <EnvelopeSimpleOpenIcon size={size} />;
		case 'pin':
			return reverse ? <PushPinSlashIcon size={size} /> : <PushPinIcon size={size} />;
		case 'mute':
			return reverse ? <BellIcon size={size} /> : <BellSlashIcon size={size} />;
		case 'archive':
			return <ArchiveIcon size={size} />;
		case 'none':
			return <ProhibitIcon size={size} />;
	}
}
